import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Waves, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { usePropiedad } from '../contexts/PropiedadContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Calendario = () => {
  const { propiedadSeleccionada, propiedades, cambiarPropiedad } = usePropiedad();
  const [reservas, setReservas] = useState([]);
  const [canales, setCanales] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [estadosReserva, setEstadosReserva] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [reservaSeleccionada, setReservaSeleccionada] = useState(null);
  const [rangoSeleccion, setRangoSeleccion] = useState({ inicio: null, fin: null });
  const [modalNuevaReserva, setModalNuevaReserva] = useState(false);
  const [modalClienteAbierto, setModalClienteAbierto] = useState(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState('');
  const [nuevoCliente, setNuevoCliente] = useState({ propiedad_id: '', nombre: '', telefono: '', email: '', notas: '' });
  const [formData, setFormData] = useState({
    huesped_nombre: '',
    huesped_telefono: '',
    huesped_email: '',
    canal_id: '',
    estado_reserva: 'Confirmada',
    monto_bruto: 0,
    comisiones: 0,
    notas: ''
  });

  useEffect(() => {
    cargarCanales();
    cargarEstadosReserva();
  }, []);

  useEffect(() => {
    if (propiedadSeleccionada?.id_propiedad) {
      cargarReservas();
      cargarClientes();
    }
  }, [propiedadSeleccionada?.id_propiedad, year]);

  const cargarCanales = async () => {
    try {
      const response = await axios.get(`${API}/canales`);
      setCanales(response.data);
    } catch (error) {
      toast.error('Error al cargar canales');
    }
  };

  const cargarEstadosReserva = async () => {
    try {
      const response = await axios.get(`${API}/estados-reserva`);
      setEstadosReserva(response.data);
    } catch (error) {
      console.error('Error al cargar estados:', error);
    }
  };

  const cargarClientes = async () => {
    if (!propiedadSeleccionada) return;
    try {
      const response = await axios.get(`${API}/clientes?propiedad_id=${propiedadSeleccionada.id_propiedad}`);
      setClientes(response.data);
    } catch (error) {
      toast.error('Error al cargar clientes');
    }
  };

  const cargarReservas = async () => {
    try {
      const response = await axios.get(`${API}/calendario/ocupacion`, {
        params: { propiedad_id: propiedadSeleccionada?.id_propiedad, year }
      });
      setReservas(response.data);
    } catch (error) {
      toast.error('Error al cargar reservas');
    }
  };

  const obtenerColorEstado = (estado) => {
    // Buscar por nombre exacto o con trim para manejar espacios
    const estadoConfig = estadosReserva.find(e => 
      e.nombre === estado || 
      e.nombre.trim() === estado.trim()
    );
    if (estadoConfig) {
      return {
        fondo: estadoConfig.color,
        texto: estadoConfig.color_texto || '#FFFFFF'
      };
    }
    // Colores por defecto si no se encuentra
    return { fondo: '#8FE0FF', texto: '#0E3A53' };
  };

  const manejarClickDia = (fechaStr) => {
    // Verificar si hay una reserva en este día
    const reservaDelDia = reservas.find(r => {
      const checkIn = new Date(r.fecha_check_in + 'T00:00:00');
      const checkOut = new Date(r.fecha_check_out + 'T00:00:00');
      const fechaActual = new Date(fechaStr + 'T00:00:00');
      return fechaActual >= checkIn && fechaActual < checkOut;
    });

    // También buscar si hay un check-out este día (para mostrar esa reserva)
    const reservaCheckOut = reservas.find(r => r.fecha_check_out === fechaStr);

    if (reservaDelDia) {
      // Si hay reserva activa, mostrarla
      setReservaSeleccionada(reservaDelDia);
      return;
    } else if (reservaCheckOut) {
      // Si hay un check-out este día, mostrar esa reserva
      setReservaSeleccionada(reservaCheckOut);
      return;
    }

    // Si no hay reserva, manejar selección de rango
    if (!rangoSeleccion.inicio) {
      // Primera fecha seleccionada
      setRangoSeleccion({ inicio: fechaStr, fin: null });
    } else if (!rangoSeleccion.fin) {
      // Segunda fecha seleccionada
      const fechaInicio = new Date(rangoSeleccion.inicio + 'T00:00:00');
      const fechaFin = new Date(fechaStr + 'T00:00:00');
      
      if (fechaFin > fechaInicio) {
        setRangoSeleccion({ inicio: rangoSeleccion.inicio, fin: fechaStr });
        abrirModalNuevaReserva(rangoSeleccion.inicio, fechaStr);
      } else {
        toast.error('La fecha de salida debe ser posterior a la de entrada');
        setRangoSeleccion({ inicio: null, fin: null });
      }
    } else {
      // Reset y comenzar nueva selección
      setRangoSeleccion({ inicio: fechaStr, fin: null });
    }
  };

  const abrirModalNuevaReserva = (fechaInicio, fechaFin) => {
    setClienteSeleccionado('');
    setFormData({
      huesped_nombre: '',
      huesped_telefono: '',
      huesped_email: '',
      canal_id: canales[0]?.id_canal || '',
      estado_reserva: 'Confirmada',
      monto_bruto: 0,
      comisiones: 0,
      notas: ''
    });
    setModalNuevaReserva(true);
  };

  const seleccionarCliente = (clienteId) => {
    setClienteSeleccionado(clienteId);
    if (clienteId) {
      const cliente = clientes.find(c => c.id_cliente === clienteId);
      if (cliente) {
        const nombreCompleto = `${cliente.nombre}${cliente.apellido ? ' ' + cliente.apellido : ''}`;
        setFormData({
          ...formData,
          huesped_nombre: nombreCompleto,
          huesped_telefono: cliente.telefono || '',
          huesped_email: cliente.email || ''
        });
      }
    }
  };

  const abrirModalNuevoCliente = () => {
    setNuevoCliente({ 
      propiedad_id: propiedadSeleccionada?.id_propiedad || '',
      nombre: '', 
      telefono: '', 
      email: '', 
      notas: '' 
    });
    setModalClienteAbierto(true);
  };

  const guardarNuevoCliente = async () => {
    try {
      const clienteData = {
        ...nuevoCliente,
        propiedad_id: propiedadSeleccionada?.id_propiedad || nuevoCliente.propiedad_id
      };
      const response = await axios.post(`${API}/clientes`, clienteData);
      toast.success('Cliente creado exitosamente');
      await cargarClientes();
      setClienteSeleccionado(response.data.id_cliente);
      setFormData({
        ...formData,
        huesped_nombre: response.data.nombre,
        huesped_telefono: response.data.telefono || '',
        huesped_email: response.data.email || ''
      });
      setModalClienteAbierto(false);
    } catch (error) {
      console.error('Error al crear cliente:', error);
      toast.error('Error al crear cliente');
    }
  };

  const guardarReserva = async () => {
    // Validar que se haya seleccionado un cliente
    if (!clienteSeleccionado) {
      toast.error('Debes seleccionar un cliente antes de crear la reserva');
      return;
    }

    try {
      const datosReserva = {
        propiedad_id: propiedadSeleccionada?.id_propiedad,
        huesped_nombre: formData.huesped_nombre,
        huesped_telefono: formData.huesped_telefono,
        huesped_email: formData.huesped_email,
        canal_id: formData.canal_id,
        fecha_check_in: rangoSeleccion.inicio,
        fecha_check_out: rangoSeleccion.fin,
        estado_reserva: formData.estado_reserva,
        monto_bruto: formData.monto_bruto,
        comisiones: formData.comisiones,
        notas: formData.notas
      };

      await axios.post(`${API}/reservas`, datosReserva);
      toast.success('Reserva creada exitosamente');
      setModalNuevaReserva(false);
      setRangoSeleccion({ inicio: null, fin: null });
      setClienteSeleccionado('');
      cargarReservas();
    } catch (error) {
      toast.error('Error al crear reserva');
      console.error(error);
    }
  };

  const cancelarSeleccion = () => {
    setRangoSeleccion({ inicio: null, fin: null });
    setModalNuevaReserva(false);
  };

  const estaEnRango = (fechaStr) => {
    if (!rangoSeleccion.inicio) return false;
    
    const fecha = new Date(fechaStr);
    const inicio = new Date(rangoSeleccion.inicio);
    
    if (rangoSeleccion.fin) {
      const fin = new Date(rangoSeleccion.fin);
      return fecha >= inicio && fecha <= fin;
    } else {
      return fecha.getTime() === inicio.getTime();
    }
  };

  const obtenerReservasDelMes = (mes) => {
    const mesStr = `${year}-${String(mes + 1).padStart(2, '0')}`;
    return reservas.filter(r => r.fecha_check_in.startsWith(mesStr));
  };

  const renderDiasDelMes = (mes) => {
    const diasEnMes = new Date(year, mes + 1, 0).getDate();
    const primerDia = new Date(year, mes, 1).getDay();
    const dias = [];

    // Espacios vacíos al inicio
    for (let i = 0; i < primerDia; i++) {
      dias.push(<div key={`empty-${i}`} className="h-12"></div>);
    }

    // Días del mes
    for (let dia = 1; dia <= diasEnMes; dia++) {
      const fechaStr = `${year}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
      
      // Buscar reserva que está activa este día (excluyendo el día de check-out)
      const reservaDelDia = reservas.find(r => {
        const checkIn = new Date(r.fecha_check_in + 'T00:00:00');
        const checkOut = new Date(r.fecha_check_out + 'T00:00:00');
        const fechaActual = new Date(fechaStr + 'T00:00:00');
        return fechaActual >= checkIn && fechaActual < checkOut;
      });

      // Buscar si hay un check-out este día
      const reservaCheckOut = reservas.find(r => r.fecha_check_out === fechaStr);
      
      // Buscar si hay un check-in este día
      const reservaCheckIn = reservas.find(r => r.fecha_check_in === fechaStr);
      
      // Determinar si es día intermedio (no check-in ni check-out)
      const esDiaIntermedio = reservaDelDia && 
        reservaDelDia.fecha_check_in !== fechaStr && 
        reservaDelDia.fecha_check_out !== fechaStr;

      const enRango = estaEnRango(fechaStr);
      
      // Si hay tanto check-out como check-in el mismo día (cambio de huésped)
      const esCambioHuesped = reservaCheckOut && reservaCheckIn && reservaCheckOut.id_reserva !== reservaCheckIn.id_reserva;
      
      dias.push(
        <div
          key={dia}
          data-testid={`calendar-day-${fechaStr}`}
          onClick={() => manejarClickDia(fechaStr)}
          className={`
            h-12 flex items-center justify-center text-sm font-medium cursor-pointer
            transition-all hover:scale-105 hover:ring-2 hover:ring-[#1AA1D8] relative overflow-hidden
            ${!reservaDelDia && !reservaCheckOut && !reservaCheckIn 
              ? enRango 
                ? 'bg-[#1AA1D8]/30 text-[#0E3A53] border-2 border-[#1AA1D8] rounded-lg' 
                : 'text-[#0E3A53] bg-[#7FFFD4]/15 rounded-lg' 
              : ''}
          `}
        >
          {/* Fondo para cambio de huésped (mitad y mitad) */}
          {esCambioHuesped ? (
            <>
              <div 
                className="absolute inset-y-0 left-0 w-1/2"
                style={{ 
                  backgroundColor: obtenerColorEstado(reservaCheckOut.estado_reserva).fondo 
                }}
              />
              <div 
                className="absolute inset-y-0 right-0 w-1/2"
                style={{ 
                  backgroundColor: obtenerColorEstado(reservaCheckIn.estado_reserva).fondo 
                }}
              />
              <span className="relative z-10 font-bold">{dia}</span>
            </>
          ) : reservaDelDia ? (
            <>
              {/* Check-in: mitad derecha pintada */}
              {reservaDelDia.fecha_check_in === fechaStr && (
                <>
                  <div className="absolute inset-y-0 left-0 w-1/2 bg-[#7FFFD4]/15" />
                  <div 
                    className="absolute inset-y-0 right-0 w-1/2 rounded-r-lg"
                    style={{ backgroundColor: obtenerColorEstado(reservaDelDia.estado_reserva).fondo }}
                  />
                </>
              )}
              {/* Check-out: mitad izquierda pintada (pero esto ya se maneja arriba con reservaCheckOut) */}
              {/* Día intermedio: todo pintado */}
              {esDiaIntermedio && (
                <div 
                  className="absolute inset-0"
                  style={{ backgroundColor: obtenerColorEstado(reservaDelDia.estado_reserva).fondo }}
                />
              )}
              <span 
                className="relative z-10"
                style={{ color: obtenerColorEstado(reservaDelDia.estado_reserva).texto }}
              >
                {dia}
              </span>
            </>
          ) : reservaCheckOut ? (
            <>
              {/* Solo check-out (sin nueva reserva entrando): mitad izquierda pintada */}
              <div 
                className="absolute inset-y-0 left-0 w-1/2 rounded-l-lg"
                style={{ backgroundColor: obtenerColorEstado(reservaCheckOut.estado_reserva).fondo }}
              />
              <div className="absolute inset-y-0 right-0 w-1/2 bg-[#7FFFD4]/15" />
              <span className="relative z-10">{dia}</span>
            </>
          ) : (
            <span>{dia}</span>
          )}
        </div>
      );
    }

    return dias;
  };

  const meses = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const diasSemana = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  return (
    <div className="space-y-6 fade-in-up">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-[#0E3A53] mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Calendario Anual
          </h1>
          <p className="text-[#0E3A53]/70">
            {rangoSeleccion.inicio && !rangoSeleccion.fin 
              ? '🖱️ Selecciona la fecha de salida' 
              : 'Haz clic en fechas disponibles para crear una reserva'}
          </p>
        </div>
        <div className="flex gap-3">
          {rangoSeleccion.inicio && !rangoSeleccion.fin && (
            <button
              onClick={cancelarSeleccion}
              className="px-4 py-2 rounded-xl bg-[#FF7F6A] text-white font-medium shadow-lg hover:shadow-xl transition-all"
              data-testid="cancel-selection-btn"
            >
              Cancelar selección
            </button>
          )}
          <select
            data-testid="select-propiedad-calendario"
            value={propiedadSeleccionada?.id_propiedad || ''}
            onChange={(e) => cambiarPropiedad(e.target.value)}
            className="px-4 py-2 rounded-xl bg-white text-[#0E3A53] font-medium shadow-lg border-2 border-[#8FE0FF] focus:outline-none focus:ring-2 focus:ring-[#1AA1D8]"
          >
            {propiedades.map(p => (
              <option key={p.id_propiedad} value={p.id_propiedad}>{p.nombre}</option>
            ))}
          </select>
          <select
            data-testid="select-year-calendario"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="px-4 py-2 rounded-xl bg-white text-[#0E3A53] font-medium shadow-lg border-2 border-[#8FE0FF] focus:outline-none focus:ring-2 focus:ring-[#1AA1D8]"
          >
            <option value={2024}>2024</option>
            <option value={2025}>2025</option>
            <option value={2026}>2026</option>
            <option value={2027}>2027</option>
            <option value={2028}>2028</option>
            <option value={2029}>2029</option>
            <option value={2030}>2030</option>
          </select>
        </div>
      </div>

      {/* Leyenda */}
      <div className="card-beach">
        <div className="flex flex-wrap gap-4">
          {estadosReserva.map(estado => (
            <div key={estado.id_estado} className="flex items-center gap-2">
              <div 
                className="w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold"
                style={{ backgroundColor: estado.color, color: estado.color_texto || '#FFFFFF' }}
              >
                
              </div>
              <span className="text-sm font-medium text-[#0E3A53]">{estado.nombre}</span>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-[#7FFFD4]/15"></div>
            <span className="text-sm font-medium text-[#0E3A53]">Disponible</span>
          </div>
        </div>
      </div>

      {/* Calendario de 12 meses */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {meses.map((nombreMes, mes) => (
          <div
            key={mes}
            className="card-beach"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1524207874394-5ec7c8c8e1a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxzYW5kJTIwdGV4dHVyZXxlbnwwfHx8fDE3NjQ5NDAyNjJ8MA&ixlib=rb-4.1.0&q=85)',
              backgroundSize: 'cover',
              backgroundBlendMode: 'overlay',
              backgroundColor: 'rgba(255, 255, 255, 0.95)'
            }}
          >
            <div className="flex items-center gap-2 mb-4">
              <Waves className="w-5 h-5 text-[#1AA1D8]" />
              <h3 className="text-lg font-bold text-[#0E3A53]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {nombreMes}
              </h3>
            </div>
            
            <div className="grid grid-cols-7 gap-1 mb-2">
              {diasSemana.map(dia => (
                <div key={dia} className="text-center text-xs font-semibold text-[#0E3A53]/60">
                  {dia}
                </div>
              ))}
            </div>
            
            <div className="grid grid-cols-7 gap-1">
              {renderDiasDelMes(mes)}
            </div>
          </div>
        ))}
      </div>

      {/* Modal de Reserva */}
      {reservaSeleccionada && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setReservaSeleccionada(null)}
        >
          <div 
            className="card-beach max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <Calendar className="w-6 h-6 text-[#1AA1D8]" />
              <h3 className="text-2xl font-bold text-[#0E3A53]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Detalles de Reserva
              </h3>
            </div>
            
            <div className="space-y-3">
              <div>
                <p className="text-sm text-[#0E3A53]/60 mb-1">Huésped</p>
                <p className="text-lg font-semibold text-[#0E3A53]">{reservaSeleccionada.huesped_nombre}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-sm text-[#0E3A53]/60 mb-1">Check-in</p>
                  <p className="text-base font-medium text-[#0E3A53]">
                    {new Date(reservaSeleccionada.fecha_check_in + 'T12:00:00').toLocaleDateString('es-CL')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-[#0E3A53]/60 mb-1">Check-out</p>
                  <p className="text-base font-medium text-[#0E3A53]">
                    {new Date(reservaSeleccionada.fecha_check_out + 'T12:00:00').toLocaleDateString('es-CL')}
                  </p>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-[#0E3A53]/60 mb-1">Noches</p>
                <p className="text-base font-medium text-[#0E3A53]">{reservaSeleccionada.noches}</p>
              </div>
              
              <div>
                <p className="text-sm text-[#0E3A53]/60 mb-1">Estado</p>
                {(() => {
                  const colores = obtenerColorEstado(reservaSeleccionada.estado_reserva);
                  return (
                    <span 
                      className="inline-block px-3 py-1 rounded-full text-sm font-medium"
                      style={{ backgroundColor: colores.fondo, color: colores.texto }}
                    >
                      {reservaSeleccionada.estado_reserva}
                    </span>
                  );
                })()}
              </div>
              
              {reservaSeleccionada.notas && (
                <div>
                  <p className="text-sm text-[#0E3A53]/60 mb-1">Notas</p>
                  <p className="text-base text-[#0E3A53]">{reservaSeleccionada.notas}</p>
                </div>
              )}
            </div>
            
            <button
              data-testid="close-modal-btn"
              onClick={() => setReservaSeleccionada(null)}
              className="mt-6 w-full bg-gradient-to-r from-[#1AA1D8] to-[#0E3A53] text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

      {/* Modal Nueva Reserva */}
      <Dialog open={modalNuevaReserva} onOpenChange={setModalNuevaReserva}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#0E3A53]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Nueva Reserva
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 p-4 bg-[#8FE0FF]/10 rounded-lg">
              <div>
                <p className="text-sm text-[#0E3A53]/60">Check-in</p>
                <p className="text-lg font-semibold text-[#0E3A53]">
                  {rangoSeleccion.inicio && new Date(rangoSeleccion.inicio).toLocaleDateString('es-CL')}
                </p>
              </div>
              <div>
                <p className="text-sm text-[#0E3A53]/60">Check-out</p>
                <p className="text-lg font-semibold text-[#0E3A53]">
                  {rangoSeleccion.fin && new Date(rangoSeleccion.fin).toLocaleDateString('es-CL')}
                </p>
              </div>
              <div className="col-span-2">
                <p className="text-sm text-[#0E3A53]/60">Noches</p>
                <p className="text-lg font-semibold text-[#1AA1D8]">
                  {rangoSeleccion.inicio && rangoSeleccion.fin && 
                    Math.ceil((new Date(rangoSeleccion.fin) - new Date(rangoSeleccion.inicio)) / (1000 * 60 * 60 * 24))} noches
                </p>
              </div>
            </div>

            {/* Selector de Cliente */}
            <div className="p-4 bg-[#8FE0FF]/10 rounded-lg border-2 border-[#1AA1D8]/20">
              <Label htmlFor="cliente-selector" className="mb-2 block font-semibold text-[#0E3A53]">Cliente *</Label>
              <p className="text-sm text-[#0E3A53]/60 mb-3">Selecciona un cliente o crea uno nuevo</p>
              <div className="flex gap-2">
                <Select value={clienteSeleccionado} onValueChange={seleccionarCliente}>
                  <SelectTrigger data-testid="select-cliente-calendario" className="flex-1">
                    <SelectValue placeholder="Seleccionar cliente..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clientes.map(cliente => (
                      <SelectItem key={cliente.id_cliente} value={cliente.id_cliente}>
                        {cliente.nombre} {cliente.apellido} {cliente.email && `(${cliente.email})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  onClick={abrirModalNuevoCliente}
                  className="bg-gradient-to-r from-[#1AA1D8] to-[#0E3A53]"
                  data-testid="btn-crear-cliente-calendario"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Crear Cliente
                </Button>
              </div>
              {clienteSeleccionado && (
                <div className="mt-3 p-3 bg-white rounded-lg border border-[#8FE0FF]">
                  <p className="text-sm font-medium text-[#0E3A53]">{formData.huesped_nombre}</p>
                  {formData.huesped_telefono && (
                    <p className="text-xs text-[#0E3A53]/70">📱 {formData.huesped_telefono}</p>
                  )}
                  {formData.huesped_email && (
                    <p className="text-xs text-[#0E3A53]/70">✉️ {formData.huesped_email}</p>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="canal">Canal</Label>
                <Select value={formData.canal_id} onValueChange={(value) => setFormData({ ...formData, canal_id: value })}>
                  <SelectTrigger data-testid="select-canal-calendario">
                    <SelectValue placeholder="Seleccionar canal" />
                  </SelectTrigger>
                  <SelectContent>
                    {canales.map(canal => (
                      <SelectItem key={canal.id_canal} value={canal.id_canal}>{canal.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="estado">Estado</Label>
                <Select value={formData.estado_reserva} onValueChange={(value) => setFormData({ ...formData, estado_reserva: value })}>
                  <SelectTrigger data-testid="select-estado-calendario">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Confirmada">Confirmada</SelectItem>
                    <SelectItem value="Pendiente">Pendiente</SelectItem>
                    <SelectItem value="Bloqueada">Bloqueada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="monto">Monto Bruto</Label>
                <Input
                  id="monto"
                  data-testid="input-monto-calendario"
                  type="number"
                  value={formData.monto_bruto || ''}
                  onChange={(e) => setFormData({ ...formData, monto_bruto: Number(e.target.value) })}
                  placeholder="Ingrese monto"
                />
              </div>
              <div>
                <Label htmlFor="comisiones">Comisiones</Label>
                <Input
                  id="comisiones"
                  data-testid="input-comisiones-calendario"
                  type="number"
                  value={formData.comisiones || ''}
                  onChange={(e) => setFormData({ ...formData, comisiones: Number(e.target.value) })}
                  placeholder="Ingrese comisión"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="notas">Notas</Label>
                <Input
                  id="notas"
                  data-testid="input-notas-calendario"
                  value={formData.notas}
                  onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                />
              </div>
            </div>
          </div>
          <div className="flex gap-3 justify-end mt-4">
            <Button variant="outline" onClick={cancelarSeleccion} data-testid="cancel-reserva-calendario">
              Cancelar
            </Button>
            <Button 
              onClick={guardarReserva}
              data-testid="save-reserva-calendario"
              className="bg-gradient-to-r from-[#1AA1D8] to-[#0E3A53]"
            >
              Crear Reserva
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Crear Cliente */}
      <Dialog open={modalClienteAbierto} onOpenChange={setModalClienteAbierto}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#0E3A53]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Nuevo Cliente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="nombre-cliente-cal">Nombre *</Label>
              <Input
                id="nombre-cliente-cal"
                data-testid="input-nombre-cliente-calendario"
                value={nuevoCliente.nombre}
                onChange={(e) => setNuevoCliente({ ...nuevoCliente, nombre: e.target.value })}
                placeholder="Nombre completo"
              />
            </div>
            <div>
              <Label htmlFor="telefono-cliente-cal">Teléfono</Label>
              <Input
                id="telefono-cliente-cal"
                data-testid="input-telefono-cliente-calendario"
                type="tel"
                value={nuevoCliente.telefono}
                onChange={(e) => setNuevoCliente({ ...nuevoCliente, telefono: e.target.value })}
                placeholder="+56 9 1234 5678"
              />
            </div>
            <div>
              <Label htmlFor="email-cliente-cal">Email</Label>
              <Input
                id="email-cliente-cal"
                data-testid="input-email-cliente-calendario"
                type="email"
                value={nuevoCliente.email}
                onChange={(e) => setNuevoCliente({ ...nuevoCliente, email: e.target.value })}
                placeholder="correo@ejemplo.com"
              />
            </div>
            <div>
              <Label htmlFor="notas-cliente-cal">Notas</Label>
              <Input
                id="notas-cliente-cal"
                data-testid="input-notas-cliente-calendario"
                value={nuevoCliente.notas}
                onChange={(e) => setNuevoCliente({ ...nuevoCliente, notas: e.target.value })}
                placeholder="Información adicional"
              />
            </div>
          </div>
          <div className="flex gap-3 justify-end mt-4">
            <Button 
              variant="outline" 
              onClick={() => setModalClienteAbierto(false)} 
              data-testid="cancel-cliente-calendario-btn"
            >
              Cancelar
            </Button>
            <Button 
              onClick={guardarNuevoCliente}
              data-testid="save-cliente-calendario-btn"
              className="bg-gradient-to-r from-[#1AA1D8] to-[#0E3A53]"
              disabled={!nuevoCliente.nombre.trim()}
            >
              Crear Cliente
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Estado vacío */}
      {reservas.length === 0 && (
        <div className="card-beach text-center py-12">
          <img 
            src="https://images.unsplash.com/photo-1509233725247-49e657c54213?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxwYWxtJTIwdHJlZXMlMjBiZWFjaHxlbnwwfHx8fDE3NjQ5NDAyNjd8MA&ixlib=rb-4.1.0&q=85"
            alt="Playa vacía"
            className="w-48 h-48 object-cover rounded-full mx-auto mb-4 shadow-xl"
          />
          <h3 className="text-2xl font-bold text-[#0E3A53] mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            No hay reservas este año
          </h3>
          <p className="text-[#0E3A53]/70">¡Aprovechemos la brisa del mar y consigamos más huéspedes!</p>
        </div>
      )}
    </div>
  );
};

export default Calendario;
